import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { namedChainsApi } from '@/features/admin/named-chains/api/named-chains-client';

interface Props {
  open: boolean;
  steps: Array<{ type: string; config: Record<string, unknown> }>;
  onClose: () => void;
  /** Called with the new named-chain ID after successful creation. */
  onCreated: (namedChainId: string) => void;
}

/**
 * SaveAsNamedDialog — prompts for a chain name, calls namedChainsApi.create,
 * and emits the new chain ID via onCreated.
 */
export function SaveAsNamedDialog({ open, steps, onClose, onCreated }: Props) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Chain name is required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const chain = await namedChainsApi.create({ name: trimmed, steps });
      onCreated(chain.id);
      setName('');
    } catch {
      setError('Failed to create named chain. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  function handleOpenChange(o: boolean) {
    if (!o) {
      setName('');
      setError('');
      onClose();
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 200,
          }}
        />
        <Dialog.Content
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'var(--color-bg-primary, #0e0e0e)',
            border: '1px solid var(--color-border, #2a2a2a)',
            borderRadius: 6,
            padding: 24,
            zIndex: 201,
            minWidth: 360,
          }}
          aria-describedby={undefined}
        >
          <Dialog.Title
            style={{
              margin: '0 0 16px',
              fontSize: 'var(--text-sm, 0.875rem)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'var(--color-text-primary, #f2f2f2)',
            }}
          >
            Save as named chain
          </Dialog.Title>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label
                htmlFor="save-as-named-chain-name"
                style={{
                  display: 'block',
                  fontSize: 'var(--text-xs, 0.8125rem)',
                  color: 'var(--color-text-muted, #888)',
                  marginBottom: 4,
                }}
              >
                Chain name
              </label>
              <input
                id="save-as-named-chain-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Date formatter"
                autoFocus
                style={{
                  width: '100%',
                  background: 'var(--color-bg-secondary, #131313)',
                  border: '1px solid var(--color-border, #2a2a2a)',
                  borderRadius: 4,
                  color: 'var(--color-text-primary, #f2f2f2)',
                  fontSize: 'var(--text-sm, 0.875rem)',
                  padding: '6px 10px',
                  boxSizing: 'border-box',
                }}
              />
              {error && (
                <p
                  style={{
                    margin: '4px 0 0',
                    fontSize: 'var(--text-xs, 0.8125rem)',
                    color: '#ef4444',
                  }}
                >
                  {error}
                </p>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Dialog.Close asChild>
                <button
                  type="button"
                  style={{
                    padding: '6px 14px',
                    border: '1px solid var(--color-border, #2a2a2a)',
                    borderRadius: 4,
                    background: 'var(--color-bg-secondary, #131313)',
                    color: 'var(--color-text-muted, #888)',
                    cursor: 'pointer',
                    fontSize: 'var(--text-xs, 0.8125rem)',
                  }}
                >
                  Cancel
                </button>
              </Dialog.Close>
              <button
                type="submit"
                disabled={saving}
                style={{
                  padding: '6px 14px',
                  border: '1px solid var(--color-accent, #fc9430)',
                  borderRadius: 4,
                  background: 'var(--color-accent, #fc9430)',
                  color: '#0e0e0e',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: 'var(--text-xs, 0.8125rem)',
                  fontWeight: 600,
                  opacity: saving ? 0.6 : 1,
                }}
              >
                {saving ? 'Creating…' : 'Create'}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
